import React, { useState, useMemo } from 'react';
import {
    Modal,
    Button,
    Radio,
    Space,
    Checkbox,
    Typography,
    Divider,
    message
} from 'antd';
import { FileExcelOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import { calculateMetricValue } from './scorecardUtils';

const { Text } = Typography;

const ScorecardsExportModal = ({
                                   visible,
                                   onClose,
                                   groupedRecords,
                                   config,
                                   sortedAgents,
                                   selectedAgents,
                                   metricMaxValues
                               }) => {
    const [exportFormat, setExportFormat] = useState('csv');
    const [selectedAgentsOnly, setSelectedAgentsOnly] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Extract metric configurations from the scorecard config
    const metrics = useMemo(() => {
        const wheelMetrics = config.metrics?.wheels || [];
        const barMetrics = config.metrics?.bars || [];
        const tagMetrics = config.metrics?.tags || [];

        return [...wheelMetrics, ...barMetrics, ...tagMetrics];
    }, [config]);

    // Get the agents to export based on selection preference
    const agentsToExport = useMemo(() => {
        if (selectedAgentsOnly && selectedAgents.length > 0) {
            return sortedAgents.filter(agent => selectedAgents.includes(agent));
        }
        return sortedAgents;
    }, [sortedAgents, selectedAgents, selectedAgentsOnly]);

    // Generate export data rows
    const generateExportData = () => {
        const data = [];

        // For each agent, create a row with all metrics
        agentsToExport.forEach((agent, index) => {
            const records = groupedRecords[agent];
            if (!records || records.length === 0) return;

            const row = {
                [config.groupByField]: agent,
                rank: index + 1,
                callCount: records.length
            };

            // Add all metrics from wheels, bars, and tags
            metrics.forEach(metric => {
                const maxValue = metric.useCustomMaxValue
                    ? metric.maxValue
                    : metricMaxValues[metric.id] || 100;

                const value = calculateMetricValue(records, metric);
                const formattedValue = metric.displayAsPercentage
                    ? `${((value / maxValue) * 100).toFixed(1)}%`
                    : value.toFixed(1);

                row[metric.label || metric.id] = value;
            });

            data.push(row);
        });

        return data;
    };

    // Convert data to CSV format
    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';

        // Extract all unique keys from the data
        const allKeys = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => allKeys.add(key));
        });

        const headers = Array.from(allKeys);

        // Create CSV header row
        let csv = headers.map(key => `"${String(key).replace(/"/g, '""')}"`).join(',') + '\\n';

        // Add data rows
        data.forEach(item => {
            const row = headers.map(key => {
                const value = item[key];

                // Handle different value types
                if (value === null || value === undefined) {
                    return '';
                } else if (typeof value === 'string') {
                    return `"${value.replace(/"/g, '""')}"`;
                } else if (typeof value === 'boolean') {
                    return value ? 'true' : 'false';
                } else if (value instanceof Date) {
                    return `"${value.toISOString()}"`;
                } else {
                    return `"${String(value).replace(/"/g, '""')}"`;
                }
            }).join(',');

            csv += row + '\\n';
        });

        return csv;
    };

    // Handle the export action
    const handleExport = () => {
        try {
            setIsExporting(true);

            const exportData = generateExportData();

            if (exportData.length === 0) {
                message.error('No data to export');
                setIsExporting(false);
                return;
            }

            let blob;
            let filename;

            if (exportFormat === 'json') {
                // Export as JSON
                const jsonStr = JSON.stringify(exportData, null, 2);
                blob = new Blob([jsonStr], { type: 'application/json' });
                filename = `scorecard_data_${new Date().toISOString().slice(0, 10)}.json`;
            } else {
                // Export as CSV
                const csvStr = convertToCSV(exportData);
                blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
                filename = `scorecard_data_${new Date().toISOString().slice(0, 10)}.csv`;
            }

            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success(`Scorecard data exported successfully as ${filename}`);
            onClose();
        } catch (error) {
            console.error('Error exporting data:', error);
            message.error('Failed to export scorecard data');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal
            title="Export Scorecard Data"
            visible={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="export"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    loading={isExporting}
                    disabled={agentsToExport.length === 0}
                >
                    Export
                </Button>
            ]}
            width={520}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Export Format Section */}
                <div>
                    <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                        Export Format
                    </Text>
                    <Radio.Group
                        value={exportFormat}
                        onChange={e => setExportFormat(e.target.value)}
                        style={{ display: 'flex', gap: '16px' }}
                    >
                        <Radio.Button value="csv" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                            <Space align="center">
                                <FileExcelOutlined style={{ fontSize: '16px', color: '#10b981' }}/>
                                <span>CSV</span>
                            </Space>
                        </Radio.Button>
                        <Radio.Button value="json" style={{ height: '40px', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                            <Space align="center">
                                <FileTextOutlined style={{ fontSize: '16px', color: '#3b82f6' }}/>
                                <span>JSON</span>
                            </Space>
                        </Radio.Button>
                    </Radio.Group>
                </div>

                <Divider style={{ margin: '0' }} />

                {/* Filter Section */}
                <div>
                    <Text style={{ fontSize: '16px', fontWeight: 500, display: 'block', marginBottom: '16px', color: '#111827' }}>
                        Export Options
                    </Text>

                    {selectedAgents.length > 0 && (
                        <Checkbox
                            checked={selectedAgentsOnly}
                            onChange={e => setSelectedAgentsOnly(e.target.checked)}
                            style={{ marginBottom: '16px' }}
                        >
                            Export selected scorecards only ({selectedAgents.length})
                        </Checkbox>
                    )}

                    <div style={{
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                    }}>
                        <Text strong>Data to be exported:</Text>
                        <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                            <li>Agent name ({config.groupByField})</li>
                            <li>Rank</li>
                            <li>Call count</li>
                            {metrics.map(metric => (
                                <li key={metric.id}>{metric.label || metric.id}</li>
                            ))}
                        </ul>
                        <Text type="secondary">
                            {agentsToExport.length} {agentsToExport.length === 1 ? 'scorecard' : 'scorecards'} will be exported
                        </Text>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ScorecardsExportModal;
